// WHICH MOUNTAIN OWNS THE CART THE DESIGN HOLDS — read from the cart's own stable metadata, never from where the visitor
// happens to be standing and never defaulted. The slide-out is mounted on every page (the homepage included), so a
// path-only reading priced a Big White basket as nothing on "/" and would have priced it as Whistler on a cold start.
//
// Evidence, in order of how stable it is:
//   · a marketplace line → the host cart item it was built from (joined by exact id, as the order planner does) → `mountain`
//   · a transport leg    → its searched corridor (`departureCode` / `arrivalCode`); the endpoint that is a mountain is the owner
//   · the /mountain-<code> route, only as a tie-break for a corridor whose two ends are both mountains
// Zero owners, or more than one, is a refusal that says so. The server still re-validates the mountain per leg.
import type { DesignCartLine } from "@/components/marketplace/DesignCartLines";
import { RESORTS } from "@/components/site/resorts";
import type { CartItem } from "@/lib/cart-store";
import { resolveStoreLineMountain } from "@/lib/design-pay-order";
import type { StoreIdentityRow } from "@/lib/design-pay-order";

const MOUNTAINS = new Set(
  RESORTS.map((r) => /^\/mountain-([a-z0-9-]+)(?:\/|$)/.exec(r.href)?.[1]).filter((c): c is string => !!c),
);

export const mountainCodeFromPath = (path: string): string => /^\/mountain-([a-z0-9-]+)(?:\/|$)/.exec(path)?.[1] ?? "";

export type CartOwner = { ok: true; mountainCode: string } | { ok: false; reason: string };

export function cartOwnerMountain({
  path,
  lines,
  hostItems,
  storeRows = [],
}: {
  path: string;
  lines: DesignCartLine[];
  hostItems: CartItem[];
  /** the live store feed as it is now (window.__SG_STORE.store): the only authority for an older line that carries no stamp */
  storeRows?: StoreIdentityRow[];
}): CartOwner {
  const routeMountain = mountainCodeFromPath(path);
  const owners = new Set<string>();
  for (const l of lines) {
    if (l.cat === "Transport") {
      const ends = [l.departureCode, l.arrivalCode].filter((c): c is string => typeof c === "string" && MOUNTAINS.has(c));
      const [only] = ends;
      if (ends.length === 1 && only) owners.add(only);
      else if (ends.length === 2 && routeMountain && ends.includes(routeMountain)) owners.add(routeMountain);
      else return { ok: false, reason: "The price could not be confirmed: a transport leg in this cart doesn't say which mountain it is for. Please pick it again from the schedule." };
    } else {
      const host = hostItems.find((h) => h.id === l.id && (h.section ?? "marketplace") !== "transport");
      // a product the design added from the live store feed carries its own mountain stamp (no host item exists for it)
      const resolved = !host ? resolveStoreLineMountain(l, storeRows) : "";
      const stamped = resolved && MOUNTAINS.has(resolved) ? resolved : "";
      if (stamped) {
        owners.add(stamped);
        continue;
      }
      if (!host?.mountain) {
        return { ok: false, reason: "The price could not be confirmed: an item in this cart isn't linked to a mountain. Remove it and add it again from its mountain page." };
      }
      owners.add(host.mountain);
    }
  }
  if (owners.size === 0) return { ok: false, reason: "The price could not be confirmed for this cart." };
  if (owners.size > 1) {
    return { ok: false, reason: "This cart mixes more than one mountain, and one order can only be for one mountain. Remove the items for the other mountain to see a price." };
  }
  const [mountainCode] = owners;
  return mountainCode ? { ok: true, mountainCode } : { ok: false, reason: "The price could not be confirmed for this cart." };
}
